import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Filter before nested slideshow initializes whenever possible.
    this.#filterByColorSlug(this.#getSelectedColorSlug());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by filtering immediately, then replacing the gallery from section HTML.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const colorSlug = this.#getColorSlugFromSelectedOptions(event.selectedOptions);
    if (colorSlug) {
      this.dataset.selectedColorSlug = colorSlug;
      this.#filterByColorSlug(colorSlug);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const zoomMedia = this.refs.zoomDialogComponent?.refs.media?.[event.detail.index];
    const mediaId = zoomMedia instanceof HTMLElement ? zoomMedia.dataset.mediaId : undefined;

    if (mediaId) {
      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Reads the selected color slug from the gallery dataset or the variant picker.
   * @returns {string}
   */
  #getSelectedColorSlug() {
    if (this.dataset.selectedColorSlug) {
      return this.dataset.selectedColorSlug;
    }

    const section = this.closest('.shopify-section, dialog, featured-product-information');
    const variantPicker = section?.querySelector('variant-picker');

    if (variantPicker && 'getAllSelectedOptions' in variantPicker) {
      return this.#getColorSlugFromSelectedOptions(
        /** @type {{ name: string, value: string }[]} */ (
          /** @type {any} */ (variantPicker).getAllSelectedOptions()
        )
      );
    }

    return '';
  }

  /**
   * Finds the Color/Colour option and converts its value to a slug.
   * @param {{ name: string, value: string }[] | undefined} selectedOptions
   * @returns {string}
   */
  #getColorSlugFromSelectedOptions(selectedOptions) {
    if (!selectedOptions?.length) return '';

    const colorOption = selectedOptions.find((option) => {
      const name = option.name?.trim().toLowerCase();
      return name === 'color' || name === 'colour';
    });

    return colorOption ? this.#slugify(colorOption.value) : '';
  }

  /**
   * Converts a variant option value into a filename-friendly slug.
   * Example: "Black Rock" -> "black-rock"
   * @param {string} value
   * @returns {string}
   */
  #slugify(value) {
    return value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Normalize strings for color matching.
   * Filenames often keep punctuation that slugify/handleize strips, e.g.
   * "G. Dyed Anthracite" -> "g-dyed-anthracite"
   * filename -> "...g.-dyed-anthracite-front..."
   * @param {string} value
   * @returns {string}
   */
  #normalizeForColorMatch(value) {
    return value.toLowerCase().replace(/\./g, '').replace(/-+/g, '-');
  }

  /**
   * Whether a media element should remain visible for the selected color.
   * Videos and 3D models always remain visible.
   * @param {HTMLElement} element
   * @param {string} colorSlug
   * @returns {boolean}
   */
  #shouldShowMedia(element, colorSlug) {
    const mediaType = element.dataset.mediaType || '';

    // Non-images (video / model / external_video) keep working as before.
    if (mediaType !== 'image') return true;
    if (!colorSlug) return true;

    const filename = element.dataset.imageFilename || '';
    const normalizedFilename = this.#normalizeForColorMatch(filename);
    const normalizedSlug = this.#normalizeForColorMatch(colorSlug);

    return (
      normalizedFilename.includes(normalizedSlug) &&
      (filename.includes('front') || filename.includes('left'))
    );
  }

  /**
   * Shows/hides gallery media, thumbnails, dots, grid items, and zoom items for a color slug.
   * Items stay in the DOM; only visibility and control wiring change.
   * @param {string} colorSlug
   */
  #filterByColorSlug(colorSlug) {
    if (!colorSlug) return;

    this.dataset.selectedColorSlug = colorSlug;

    const gallerySlides = this.querySelectorAll(':scope slideshow-slide');
    const gridItems = this.querySelectorAll(':scope .media-gallery__grid > li');
    const zoomMediaItems = this.querySelectorAll(':scope zoom-dialog .dialog-zoomed-gallery > li');
    const zoomThumbnails = this.querySelectorAll(':scope zoom-dialog .dialog-thumbnails-list__thumbnail');

    for (const slide of gallerySlides) {
      if (!(slide instanceof HTMLElement)) continue;
      slide.toggleAttribute('hidden', !this.#shouldShowMedia(slide, colorSlug));
      slide.removeAttribute('reveal');
    }

    for (const item of [...gridItems, ...zoomMediaItems, ...zoomThumbnails]) {
      if (!(item instanceof HTMLElement)) continue;
      item.toggleAttribute('hidden', !this.#shouldShowMedia(item, colorSlug));
    }

    this.#syncSlideshowControls(colorSlug);
    this.#selectFirstVisibleSlide();
  }

  /**
   * Keeps thumbnail/dot refs aligned with visible slides and updates the counter.
   * @param {string} colorSlug
   */
  #syncSlideshowControls(colorSlug) {
    const controlRoots = this.querySelectorAll(':scope slideshow-controls');

    for (const controls of controlRoots) {
      const thumbnails = controls.querySelectorAll('.slideshow-controls__thumbnail');
      const dotItems = controls.querySelectorAll('.slideshow-controls__dots > li');

      let visibleIndex = 0;

      thumbnails.forEach((thumbnail) => {
        if (!(thumbnail instanceof HTMLElement)) return;

        const show = this.#shouldShowMedia(thumbnail, colorSlug);
        thumbnail.toggleAttribute('hidden', !show);

        if (show) {
          thumbnail.setAttribute('ref', 'thumbnails[]');
          thumbnail.setAttribute('on:click', `/select/${visibleIndex}`);
          thumbnail.setAttribute('aria-selected', visibleIndex === 0 ? 'true' : 'false');
          visibleIndex += 1;
        } else {
          thumbnail.removeAttribute('ref');
          thumbnail.removeAttribute('on:click');
          thumbnail.removeAttribute('aria-selected');
        }
      });

      visibleIndex = 0;
      dotItems.forEach((dotItem) => {
        if (!(dotItem instanceof HTMLElement)) return;

        const show = this.#shouldShowMedia(dotItem, colorSlug);
        const button = dotItem.querySelector('button');
        dotItem.toggleAttribute('hidden', !show);

        if (!(button instanceof HTMLElement)) return;

        if (show) {
          button.setAttribute('ref', 'dots[]');
          button.setAttribute('on:click', `/select/${visibleIndex}`);
          button.setAttribute('aria-selected', visibleIndex === 0 ? 'true' : 'false');
          visibleIndex += 1;
        } else {
          button.removeAttribute('ref');
          button.removeAttribute('on:click');
          button.removeAttribute('aria-selected');
        }
      });

      const total = controls.querySelector('[data-slideshow-total]');
      if (total) {
        const visibleSlideCount = [...this.querySelectorAll(':scope slideshow-slide')].filter(
          (slide) => slide instanceof HTMLElement && this.#shouldShowMedia(slide, colorSlug)
        ).length;
        total.textContent = String(visibleSlideCount);
      }
    }

    // Refresh slideshow refs after adding/removing control refs.
    this.slideshow?.updatedCallback?.();
  }

  /**
   * Selects the first visible slide after filtering.
   */
  #selectFirstVisibleSlide() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const firstVisible = [...this.querySelectorAll(':scope slideshow-slide')].find(
      (slide) => slide instanceof HTMLElement && !slide.hasAttribute('hidden')
    );

    if (!(firstVisible instanceof HTMLElement)) return;

    const slideId = firstVisible.getAttribute('slide-id');

    // Wait a frame so slideshow scroll/setup can finish after connect + ref updates.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (slideId) {
        slideshow.select?.({ id: slideId }, undefined, { animate: false });
      } else {
        slideshow.select?.(0, undefined, { animate: false });
      }
    });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
